import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Platform
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList, Transaction } from '../types';
import { moderationService, ReportedItem } from '../services/moderation';
import { transactionsService } from '../services/transactions';
import { imageService } from '../services/images';
import { useDialog } from '../contexts';
import { useTranslation } from 'react-i18next';
import { Button } from '../components';
import { Ionicons } from '@expo/vector-icons';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminModeration'>;

type TabType = 'items' | 'disputes';

export const AdminModerationScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [activeTab, setActiveTab] = useState<TabType>('items');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reportedItems, setReportedItems] = useState<ReportedItem[]>([]);
  const [disputedTransactions, setDisputedTransactions] = useState<Transaction[]>([]);
  const [processingItem, setProcessingItem] = useState<string | null>(null);

  const loadReportedItems = async () => {
    try {
      const items = await moderationService.getReportedItems();
      setReportedItems(items);
    } catch (error: any) {
      dialog.alert(
        t('common.errors.error'),
        error.message || t('common.errors.loadFailed'),
        'error'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadDisputedTransactions = async () => {
    try {
      const transactions = await transactionsService.getDisputedTransactions();
      setDisputedTransactions(transactions);
    } catch (error: any) {
      dialog.alert(
        t('common.errors.error'),
        error.message || t('common.errors.loadFailed'),
        'error'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'items') {
      loadReportedItems();
    } else {
      loadDisputedTransactions();
    }
  }, [activeTab]);

  const handleRefresh = () => {
    setRefreshing(true);
    if (activeTab === 'items') {
      loadReportedItems();
    } else {
      loadDisputedTransactions();
    }
  };

  const handleApprove = async (itemId: string, itemTitle: string) => {
    const confirmed = await dialog.confirm(
      t('screens.adminModeration.approveTitle'),
      t('screens.adminModeration.approveMessage', { title: itemTitle }),
      { variant: 'primary' }
    );

    if (!confirmed) return;

    setProcessingItem(itemId);
    try {
      await moderationService.approveItem(itemId, 'Reviewed and approved by admin');
      dialog.alert(
        t('common.success'),
        t('screens.adminModeration.approveSuccess'),
        'success'
      );
      // Remove from list
      setReportedItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error: any) {
      dialog.alert(
        t('common.errors.error'),
        error.message || t('common.errors.operationFailed'),
        'error'
      );
    } finally {
      setProcessingItem(null);
    }
  };

  const handleDelete = async (itemId: string, itemTitle: string) => {
    const confirmed = await dialog.confirm(
      t('screens.adminModeration.deleteTitle'),
      t('screens.adminModeration.deleteMessage', { title: itemTitle }),
      { variant: 'danger' }
    );

    if (!confirmed) return;

    setProcessingItem(itemId);
    try {
      await moderationService.deleteReportedItem(itemId, 'Inappropriate content removed');
      dialog.alert(
        t('common.success'),
        t('screens.adminModeration.deleteSuccess'),
        'success'
      );
      // Remove from list
      setReportedItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error: any) {
      dialog.alert(
        t('common.errors.error'),
        error.message || t('common.errors.operationFailed'),
        'error'
      );
    } finally {
      setProcessingItem(null);
    }
  };

  const handleViewDetails = (itemId: string) => {
    navigation.navigate('ItemDetails', { itemId });
  };

  const handleApprovePayment = async (transactionId: string, itemTitle: string) => {
    const confirmed = await dialog.confirm(
      t('screens.adminModeration.disputes.approveTitle'),
      t('screens.adminModeration.disputes.approveMessage', { title: itemTitle }),
      { variant: 'primary' }
    );

    if (!confirmed) return;

    setProcessingItem(transactionId);
    try {
      await transactionsService.resolveDispute(transactionId, 'approve');
      dialog.alert(
        t('common.success'),
        t('screens.adminModeration.disputes.approveSuccess'),
        'success'
      );
      // Remove from list
      setDisputedTransactions(prev => prev.filter(tx => tx.id !== transactionId));
    } catch (error: any) {
      dialog.alert(
        t('common.errors.error'),
        error.message || t('common.errors.operationFailed'),
        'error'
      );
    } finally {
      setProcessingItem(null);
    }
  };

  const handleIssueRefund = async (transactionId: string, itemTitle: string) => {
    const confirmed = await dialog.confirm(
      t('screens.adminModeration.disputes.refundTitle'),
      t('screens.adminModeration.disputes.refundMessage', { title: itemTitle }),
      { variant: 'danger' }
    );

    if (!confirmed) return;

    setProcessingItem(transactionId);
    try {
      await transactionsService.resolveDispute(transactionId, 'refund');
      dialog.alert(
        t('common.success'),
        t('screens.adminModeration.disputes.refundSuccess'),
        'success'
      );
      // Remove from list
      setDisputedTransactions(prev => prev.filter(tx => tx.id !== transactionId));
    } catch (error: any) {
      dialog.alert(
        t('common.errors.error'),
        error.message || t('common.errors.operationFailed'),
        'error'
      );
    } finally {
      setProcessingItem(null);
    }
  };

  const handleViewChat = (conversationId: string) => {
    navigation.navigate('Chat', { conversationId });
  };

  const renderReportReason = (reason: string) => {
    const reasonLabels: Record<string, string> = {
      inappropriate: t('screens.adminModeration.reasons.inappropriate'),
      scam: t('screens.adminModeration.reasons.scam'),
      copyright: t('screens.adminModeration.reasons.copyright'),
      other: t('screens.adminModeration.reasons.other')
    };
    return reasonLabels[reason] || reason;
  };

  const renderDisputeReason = (reason: string) => {
    const reasonLabels: Record<string, string> = {
      mismatch: t('screens.adminModeration.disputes.reasons.mismatch'),
      noShow: t('screens.adminModeration.disputes.reasons.noShow'),
      damaged: t('screens.adminModeration.disputes.reasons.damaged'),
      other: t('screens.adminModeration.disputes.reasons.other')
    };
    return reasonLabels[reason] || reason;
  };

  const renderReportedItem = ({ item }: { item: ReportedItem }) => {
    const isProcessing = processingItem === item.id;
    const firstImage = item.image_urls?.[0];

    return (
      <View style={styles.itemCard}>
        <View style={styles.itemHeader}>
          <View style={styles.itemInfo}>
            {firstImage && (
              <Image
                source={{ uri: imageService.getThumbnailUrl(firstImage) }}
                style={styles.thumbnail}
              />
            )}
            <View style={styles.itemDetails}>
              <Text style={styles.itemTitle} numberOfLines={2}>
                {item.title}
              </Text>
              <Text style={styles.itemPrice}>
                €{item.price.toFixed(2)}
              </Text>
              <Text style={styles.sellerInfo}>
                {t('screens.adminModeration.seller')}: {item.seller.name}
              </Text>
            </View>
          </View>
          <View style={styles.reportBadge}>
            <Text style={styles.reportCount}>{item.report_count}</Text>
            <Text style={styles.reportLabel}>
              {t('screens.adminModeration.reports')}
            </Text>
          </View>
        </View>

        <View style={styles.reportsSection}>
          <Text style={styles.reportsTitle}>
            {t('screens.adminModeration.reportDetails')}:
          </Text>
          {item.reports.map((report, index) => (
            <View key={report.id} style={styles.reportItem}>
              <Text style={styles.reportReason}>
                • {renderReportReason(report.reason)}
              </Text>
              {report.details && (
                <Text style={styles.reportDetails}>{report.details}</Text>
              )}
            </View>
          ))}
        </View>

        <View style={styles.actionsRow}>
          <Button
            title={t('screens.adminModeration.viewItem')}
            onPress={() => handleViewDetails(item.id)}
            variant="secondary"
            style={styles.actionButton}
            disabled={isProcessing}
          />
          <Button
            title={t('screens.adminModeration.approve')}
            onPress={() => handleApprove(item.id, item.title)}
            variant="primary"
            style={styles.actionButton}
            disabled={isProcessing}
          />
          <Button
            title={t('screens.adminModeration.delete')}
            onPress={() => handleDelete(item.id, item.title)}
            variant="outline"
            style={styles.actionButton}
            disabled={isProcessing}
          />
        </View>
        {isProcessing && (
          <View style={styles.processingOverlay}>
            <ActivityIndicator size="small" color="#0066CC" />
          </View>
        )}
      </View>
    );
  };

  const renderDisputedTransaction = ({ item }: { item: Transaction }) => {
    const isProcessing = processingItem === item.id;
    const firstImage = item.item?.image_urls?.[0];

    return (
      <View style={styles.itemCard}>
        <View style={styles.itemHeader}>
          <View style={styles.itemInfo}>
            {firstImage && (
              <Image
                source={{ uri: imageService.getThumbnailUrl(firstImage) }}
                style={styles.thumbnail}
              />
            )}
            <View style={styles.itemDetails}>
              <Text style={styles.itemTitle} numberOfLines={2}>
                {item.item?.title || t('screens.adminModeration.disputes.unknownItem')}
              </Text>
              <Text style={styles.itemPrice}>
                €{item.total_amount.toFixed(2)}
              </Text>
              <Text style={styles.sellerInfo}>
                {t('screens.adminModeration.disputes.buyer')}: {item.buyer?.first_name} {item.buyer?.last_name}
              </Text>
              <Text style={styles.sellerInfo}>
                {t('screens.adminModeration.disputes.seller')}: {item.seller?.first_name} {item.seller?.last_name}
              </Text>
            </View>
          </View>
          <View style={styles.disputeBadge}>
            <Ionicons name="warning" size={32} color="#FFFFFF" />
            <Text style={styles.disputeLabel}>
              {t('screens.adminModeration.disputes.disputed')}
            </Text>
          </View>
        </View>

        <View style={styles.reportsSection}>
          <Text style={styles.reportsTitle}>
            {t('screens.adminModeration.disputes.disputeDetails')}:
          </Text>
          {item.dispute_reason && (
            <View style={styles.reportItem}>
              <Text style={styles.reportReason}>
                • {renderDisputeReason(item.dispute_reason)}
              </Text>
            </View>
          )}
          {item.dispute_created_at && (
            <Text style={styles.disputeDate}>
              {t('screens.adminModeration.disputes.reportedOn')}: {new Date(item.dispute_created_at).toLocaleDateString()}
            </Text>
          )}
        </View>

        <View style={styles.actionsRow}>
          <Button
            title={t('screens.adminModeration.disputes.viewChat')}
            onPress={() => handleViewChat(item.conversation_id)}
            variant="secondary"
            style={styles.actionButton}
            disabled={isProcessing}
          />
          <Button
            title={t('screens.adminModeration.disputes.approvePayment')}
            onPress={() => handleApprovePayment(item.id, item.item?.title || '')}
            variant="primary"
            style={styles.actionButton}
            disabled={isProcessing}
          />
          <Button
            title={t('screens.adminModeration.disputes.issueRefund')}
            onPress={() => handleIssueRefund(item.id, item.item?.title || '')}
            variant="outline"
            style={styles.actionButton}
            disabled={isProcessing}
          />
        </View>
        {isProcessing && (
          <View style={styles.processingOverlay}>
            <ActivityIndicator size="small" color="#0066CC" />
          </View>
        )}
      </View>
    );
  };

  const renderHeader = () => {
    const count = activeTab === 'items' ? reportedItems.length : disputedTransactions.length;
    const subtitleKey = activeTab === 'items'
      ? 'screens.adminModeration.subtitle'
      : 'screens.adminModeration.disputes.subtitle';

    return (
      <View style={[styles.header, Platform.OS === 'web' && styles.headerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}
          >
            <Ionicons name="arrow-back" size={24} color="#007AFF" />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {t('screens.adminModeration.title')}
          </Text>
          <Text style={styles.headerSubtitle}>
            {t(subtitleKey, { count })}
          </Text>
          <View style={styles.tabsContainer}>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'items' && styles.activeTab]}
              onPress={() => setActiveTab('items')}
            >
              <Text style={[styles.tabText, activeTab === 'items' && styles.activeTabText]}>
                {t('screens.adminModeration.tabs.items')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'disputes' && styles.activeTab]}
              onPress={() => setActiveTab('disputes')}
            >
              <Text style={[styles.tabText, activeTab === 'disputes' && styles.activeTabText]}>
                {t('screens.adminModeration.tabs.disputes')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.container}>
        {renderHeader()}
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#0066CC" />
          <Text style={styles.loadingText}>
            {t('screens.adminModeration.loading')}
          </Text>
        </View>
      </View>
    );
  }

  const currentData = activeTab === 'items' ? reportedItems : disputedTransactions;
  const isEmpty = currentData.length === 0;

  if (isEmpty) {
    const emptyTitleKey = activeTab === 'items'
      ? 'screens.adminModeration.noReports'
      : 'screens.adminModeration.disputes.noDisputes';
    const emptyDescKey = activeTab === 'items'
      ? 'screens.adminModeration.noReportsDescription'
      : 'screens.adminModeration.disputes.noDisputesDescription';

    return (
      <View style={styles.container}>
        {renderHeader()}
        <View style={styles.centerContainer}>
          <Text style={styles.emptyIcon}>✓</Text>
          <Text style={styles.emptyTitle}>
            {t(emptyTitleKey)}
          </Text>
          <Text style={styles.emptyDescription}>
            {t(emptyDescKey)}
          </Text>
          <Button
            title={t('common.buttons.refresh')}
            onPress={handleRefresh}
            variant="secondary"
            style={styles.refreshButton}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {renderHeader()}
      {activeTab === 'items' ? (
        <FlatList
          data={reportedItems}
          renderItem={renderReportedItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          refreshing={refreshing}
          onRefresh={handleRefresh}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />
      ) : (
        <FlatList
          data={disputedTransactions}
          renderItem={renderDisputedTransaction}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          refreshing={refreshing}
          onRefresh={handleRefresh}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5'
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    padding: 20
  },
  header: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0'
  },
  headerWeb: {
    alignItems: 'center' as const,
  },
  headerContent: {
    width: '100%',
    maxWidth: 1200,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8
  },
  backText: {
    fontSize: 16,
    color: '#007AFF'
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 4
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666666'
  },
  listContent: {
    padding: 16,
    ...(Platform.OS === 'web' && {
      alignItems: 'center' as const,
    }),
  },
  contentContainer: {
    width: '100%',
    ...(Platform.OS === 'web' && {
      maxWidth: 1200,
    }),
  },
  itemCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    ...(Platform.OS === 'web' && {
      maxWidth: 1200,
      width: '100%',
      alignSelf: 'center' as const,
    }),
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12
  },
  itemInfo: {
    flexDirection: 'row',
    flex: 1
  },
  thumbnail: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: '#E0E0E0',
    marginRight: 12
  },
  itemDetails: {
    flex: 1,
    justifyContent: 'center'
  },
  itemTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333333',
    marginBottom: 4
  },
  itemPrice: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0066CC',
    marginBottom: 4
  },
  sellerInfo: {
    fontSize: 12,
    color: '#666666'
  },
  reportBadge: {
    backgroundColor: '#FF4444',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 60
  },
  reportCount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF'
  },
  reportLabel: {
    fontSize: 10,
    color: '#FFFFFF',
    textTransform: 'uppercase'
  },
  reportsSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0'
  },
  reportsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333333',
    marginBottom: 8
  },
  reportItem: {
    marginBottom: 6
  },
  reportReason: {
    fontSize: 14,
    color: '#FF4444',
    fontWeight: '500'
  },
  reportDetails: {
    fontSize: 12,
    color: '#666666',
    marginLeft: 12,
    marginTop: 2
  },
  actionsRow: {
    flexDirection: 'row',
    marginTop: 16,
    gap: 8
  },
  actionButton: {
    flex: 1
  },
  processingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12
  },
  separator: {
    height: 16
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#666666'
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 16
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 8
  },
  emptyDescription: {
    fontSize: 14,
    color: '#666666',
    textAlign: 'center',
    marginBottom: 24
  },
  refreshButton: {
    minWidth: 150
  },
  tabsContainer: {
    flexDirection: 'row',
    marginTop: 16,
    gap: 8
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: '#F5F5F5',
    alignItems: 'center'
  },
  activeTab: {
    backgroundColor: '#0066CC'
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666666'
  },
  activeTabText: {
    color: '#FFFFFF'
  },
  disputeBadge: {
    backgroundColor: '#FF9500',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 60
  },
  disputeLabel: {
    fontSize: 10,
    color: '#FFFFFF',
    textTransform: 'uppercase',
    marginTop: 4
  },
  disputeDate: {
    fontSize: 12,
    color: '#999999',
    marginTop: 8,
    fontStyle: 'italic'
  }
});
